import type { Coordinates, LocationWithDistance, GeocodingResult } from '@/types'

// Haversine-formule voor het berekenen van de afstand tussen twee punten op aarde
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371 // Straal van de aarde in kilometers
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

// Geocoding service - gebruikt OpenStreetMap Nominatim API
export async function geocodeAddress(query: string): Promise<GeocodingResult | null> {
  try {
    const encodedQuery = encodeURIComponent(query)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedQuery}&countrycodes=nl&limit=1`,
      {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'MQ-Vue-Arbo-Inschrijfform'
        }
      }
    )

    if (!response.ok) {
      throw new Error(`Geocoding request failed with status ${response.status}`)
    }

    const data = await response.json()
    
    if (data && data.length > 0) {
      const result = data[0]
      return {
        coordinates: {
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon)
        },
        formattedAddress: result.display_name
      }
    }
    
    return null
  } catch (error) {
    console.error('Geocoding error:', error)
    return null
  }
}

// Filter locaties op basis van afstand
export function filterLocationsByDistance(
  locations: LocationWithDistance[],
  radius: number
): LocationWithDistance[] {
  return locations
    .filter(location => location.distance <= radius)
    .sort((a, b) => a.distance - b.distance)
}

// Bereken afstanden voor alle locaties vanaf een centraal punt
export function calculateDistancesForLocations(
  centerCoordinates: Coordinates,
  locations: Array<{ id: string; name: string; coordinates: Coordinates }>
): LocationWithDistance[] {
  return locations.map(location => ({
    ...location,
    distance: calculateDistance(
      centerCoordinates.latitude,
      centerCoordinates.longitude,
      location.coordinates.latitude,
      location.coordinates.longitude
    )
  }))
}

// Locaties worden nu dynamisch geladen uit ACF options via REST API

// Formatteer afstand voor weergave
export function formatDistance(distance: number): string {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`
  } else if (distance < 10) {
    return `${distance.toFixed(1)} km`
  } else {
    return `${Math.round(distance)} km`
  }
} 